<!--
  @component DeployRunEdge — E2 effecting composition.
  composes: TriggerIO + PreviewPlanIO + GatesIO + ApprovalsIO +
            CutoverIO + ApprovalsIO + (Approvals.pending via stream).
  effects:  preview / gate / trigger / cutover / approve|deny — all
            delegated to portal action methods (never parent.dispatch
            directly; the two-plane wall + FACE-LEDGER stay at the
            node).
  purpose:  one unified state-machine surface for "do a deploy".
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { DEPLOY_RUN_EDGE_KEY } from "./deployment-edges.js";
  import type {
    DeployRunEdgeContext,
    DeployRunStage,
  } from "./deployment-edges.js";
  import type {
    DeployTargetRef,
    SupplyRunSpec,
  } from "../../nodes/deployment/deployment-io.js";

  export { DEPLOY_RUN_EDGE_KEY };
  export type { DeployRunEdgeContext, DeployRunStage };

  export function useDeployRunEdge(): DeployRunEdgeContext {
    const ctx = getContext<DeployRunEdgeContext | undefined>(
      DEPLOY_RUN_EDGE_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useDeployRunEdge() called outside of a DeployRunEdge tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useTriggerIO } from "../../nodes/deployment/TriggerIO.svelte";
  import { usePreviewPlanIO } from "../../nodes/deployment/PreviewPlanIO.svelte";
  import { useGatesIO } from "../../nodes/deployment/GatesIO.svelte";
  import { useApprovalsIO } from "../../nodes/deployment/ApprovalsIO.svelte";
  import { useCutoverIO } from "../../nodes/deployment/CutoverIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const trigger = useTriggerIO();
  const preview = usePreviewPlanIO();
  const gates = useGatesIO();
  const approvals = useApprovalsIO();
  const cutover = useCutoverIO();

  let stage: DeployRunStage = $state("idle");
  let currentSpec: SupplyRunSpec | null = $state(null);
  let currentDiff: unknown = $state(null);
  let gateVerdict: unknown = $state(null);
  let error: Error | null = $state(null);

  function recordFail(e: unknown): never {
    const err = e instanceof Error ? e : new Error(String(e));
    error = err;
    stage = "failed";
    throw err;
  }

  async function prepare(spec: SupplyRunSpec): Promise<unknown> {
    currentSpec = spec;
    error = null;
    stage = "previewing";
    try {
      const diff = await preview.preview(spec);
      currentDiff = diff;
      stage = "gated";
      return diff;
    } catch (e) {
      recordFail(e);
    }
  }

  async function gate(spec: SupplyRunSpec): Promise<unknown> {
    if (stage !== "gated") {
      await prepare(spec);
    }
    error = null;
    try {
      const verdict = await gates.gatedDeploy(spec);
      gateVerdict = verdict;
      return verdict;
    } catch (e) {
      recordFail(e);
    }
  }

  async function run(spec: SupplyRunSpec): Promise<unknown> {
    currentSpec = spec;
    error = null;
    try {
      stage = "previewing";
      currentDiff = await preview.preview(spec);
      stage = "gated";
      gateVerdict = await gates.gatedDeploy(spec);
      stage = "running";
      const result = await trigger.trigger(spec);
      stage = "succeeded";
      return result;
    } catch (e) {
      recordFail(e);
    }
  }

  async function doCutover(
    target: DeployTargetRef,
    options?: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    error = null;
    stage = "cutover";
    try {
      const result = await cutover.cutover(target, options);
      stage = "succeeded";
      return result;
    } catch (e) {
      recordFail(e);
    }
  }

  async function decideApproval(
    approvalId: string,
    decision: "approve" | "deny",
    note?: string,
  ): Promise<unknown> {
    if (decision === "approve") {
      return approvals.approve(approvalId, note);
    }
    // deny requires a reason — fall back to "denied" if no note.
    return approvals.deny(approvalId, note ?? "denied");
  }

  function abort(): void {
    stage = "idle";
    currentSpec = null;
    currentDiff = null;
    gateVerdict = null;
    error = null;
  }

  const ctx: DeployRunEdgeContext = {
    get stage() {
      return stage;
    },
    get currentSpec() {
      return currentSpec;
    },
    get currentDiff() {
      return currentDiff;
    },
    get gateVerdict() {
      return gateVerdict;
    },
    get error() {
      return error;
    },
    get inFlight() {
      return (
        preview.inFlight ||
        gates.inFlight ||
        trigger.inFlight ||
        approvals.inFlight ||
        cutover.inFlight
      );
    },
    get pendingApprovals() {
      return approvals.pending;
    },
    prepare,
    gate,
    run,
    decideApproval,
    cutover: doCutover,
    abort,
  } satisfies DeployRunEdgeContext;

  setContext(DEPLOY_RUN_EDGE_KEY, ctx);
</script>

{@render children?.()}
